import {
  wiseOwlEndpoints,
  WiseOwlCreateChatArgs,
  WiseOwlGetChatByIdArgs,
  WiseOwlUpdateChatArgs,
  WiseOwlCreateFeedbackArgs,
  WiseOwlChatType,
  WiseOwlCreateChatResponseData,
  WiseOwlUpdateChatResponseData,
  WiseOwlGetChatSummaryByIdArgs,
  WiseOwlGetUngatedChatSummaryByIdArgs,
  WiseOwlGetAccessTokeByIngressArgs,
  WiseOwlGetAccessTokenResponseData,
  WiseOwlGetChatAboutTicketIdArgs,
  WiseOwlGetChatAboutTicketIdResponseData,
  WiseOwlIngress,
  wiseOwlIngressToServiceEndpointMap,
  WiseOwlGetChatSummaryByIdResponseData,
  WiseOwlGetHomeArgs,
  WiseOwlGetHomeResponseData,
  WiseOwlTransferToLiveChatArgs,
  WiseOwlTransferToLiveChatResponseData,
  RecaptchaAction,
} from "@twiliointernal/e2-commons-shared-definitions";
import { getRecaptchaToken } from "@twiliointernal/e2-commons-shared-utils";

import Client from "./client";

class WiseOwlApi extends Client {
  constructor(ingress: WiseOwlIngress) {
    const serviceEndpoint = wiseOwlIngressToServiceEndpointMap[ingress];
    super(serviceEndpoint, ingress);
  }
  /**
   * AUTH
   * - Only used for ZD/SF
   */
  getAuthToken = async ({
    headers,
    requestOptions,
    ingress,
  }: WiseOwlGetAccessTokeByIngressArgs) => {
    const response = await this.get<WiseOwlGetAccessTokenResponseData>({
      path: wiseOwlEndpoints.getAccessToken(ingress),
      headers,
      requestOptions,
    });

    return response;
  };

  /**
   * GATED METHODS
   */
  getHome = async ({
    applicationId,
    stargateUngatedUserId,
    headers: extraHeaders,
  }: WiseOwlGetHomeArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );

    const path = applicationId
      ? wiseOwlEndpoints.getHomeByApplicationId(applicationId)
      : stargateUngatedUserId
      ? wiseOwlEndpoints.ungatedGetHome(stargateUngatedUserId)
      : wiseOwlEndpoints.getHome;

    const response = await this.get<WiseOwlGetHomeResponseData>({
      path,
      headers,
      requestOptions,
    });

    return response;
  };

  getChatById = async ({
    applicationId,
    interruptedChatId,
    chatId,
    headers: extraHeaders,
  }: WiseOwlGetChatByIdArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );

    const path = wiseOwlEndpoints.getChatById(chatId, {
      applicationId,
      interruptedChatId,
    });

    const response = await this.get<WiseOwlChatType>({
      path,
      headers,
      requestOptions,
    });

    return response;
  };

  getChatByAboutTicketId = async ({
    aboutTicketId,
    headers: extraHeaders,
  }: WiseOwlGetChatAboutTicketIdArgs) => {
    const { headers } = await this.getRequestOptions(extraHeaders);

    const response = await this.get<WiseOwlGetChatAboutTicketIdResponseData>({
      path: `${wiseOwlEndpoints.getChats({ aboutTicketId })}`,
      headers,
    });

    return response;
  };

  createChat = async ({
    ingress,
    body,
    headers: extraHeaders,
  }: WiseOwlCreateChatArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );
    const isUngated = ingress === WiseOwlIngress.oscUngated;
    const path = isUngated
      ? wiseOwlEndpoints.ungatedCreateChat
      : wiseOwlEndpoints.createChat;

    const requestBody = isUngated
      ? {
          ...body,
          recaptchaToken: await getRecaptchaToken(RecaptchaAction.UNGATED_CHAT),
        }
      : body;

    const response = await this.post<WiseOwlCreateChatResponseData>({
      path,
      headers,
      requestOptions,
      body: JSON.stringify(requestBody),
    });

    return response;
  };

  updateChat = async ({
    ingress,
    chatId,
    body,
    headers: extraHeaders,
  }: WiseOwlUpdateChatArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );
    const isUngated = ingress === WiseOwlIngress.oscUngated;
    const path = isUngated
      ? wiseOwlEndpoints.ungatedUpdateChat(chatId)
      : wiseOwlEndpoints.updateChat(chatId);

    const requestBody = isUngated
      ? {
          ...body,
          recaptchaToken: await getRecaptchaToken(RecaptchaAction.UNGATED_CHAT),
        }
      : body;

    const response = await this.put<WiseOwlUpdateChatResponseData>({
      path,
      headers,
      requestOptions,
      body: JSON.stringify(requestBody),
    });

    return response;
  };

  createFeedback = async ({
    ingress,
    chatId,
    messageId,
    body,
    headers: extraHeaders,
  }: WiseOwlCreateFeedbackArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );
    const isUngated = ingress === WiseOwlIngress.oscUngated;
    const path = isUngated
      ? wiseOwlEndpoints.ungatedCreateFeedback(chatId, messageId)
      : wiseOwlEndpoints.createFeedback(chatId, messageId);

    const requestBody = isUngated
      ? {
          ...body,
          recaptchaToken: await getRecaptchaToken(
            RecaptchaAction.UNGATED_FEEDBACK
          ),
        }
      : body;

    const response = await this.post<Record<string, never>>({
      path,
      headers,
      requestOptions,
      body: JSON.stringify(requestBody),
    });

    return response;
  };

  getChatSummaryById = async ({
    chatId,
    headers: extraHeaders,
  }: WiseOwlGetChatSummaryByIdArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );

    const response = await this.get<WiseOwlGetChatSummaryByIdResponseData>({
      path: wiseOwlEndpoints.getChatSummaryById(chatId),
      headers,
      requestOptions,
    });

    return response;
  };

  transferToLiveChat = async ({
    chatId,
    body,
    headers: extraHeaders,
  }: WiseOwlTransferToLiveChatArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );

    const response = await this.post<WiseOwlTransferToLiveChatResponseData>({
      path: wiseOwlEndpoints.transferToLiveChat(chatId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };

  /**
   * UNGATED METHODS
   */
  // TODO: Remove this method once support-api is updated to GET or pivot to WO API via Stargate
  ungatedGetChatSummaryById = async ({
    chatId,
    body,
    headers: extraHeaders,
  }: WiseOwlGetUngatedChatSummaryByIdArgs) => {
    const { headers, requestOptions } = await this.getRequestOptions(
      extraHeaders
    );

    const response = await this.post<WiseOwlGetChatSummaryByIdResponseData>({
      path: wiseOwlEndpoints.ungatedGetChatSummaryById(chatId),
      headers,
      requestOptions,
      body: JSON.stringify(body),
    });

    return response;
  };
}

export default WiseOwlApi;
